import { Injectable, inject } from '@angular/core';
import { HttpErrorResponse } from '@angular/common/http';
import { Action, Selector, State, StateContext } from '@ngxs/store';
import { Observable, catchError, tap, throwError } from 'rxjs';
import { StateApiError, checkForBadHttpErrorMessage, checkToAddStateApiError, checkToRemoveStateApiError } from '@trovata/app/core/models/state-error.model';
import { StablecoinTransfersService } from '../../../services/transfers/transfers.service';
import { StablecoinPage } from '../../../models/stablecoin.model';
import { StablecoinApprovalPolicy, StablecoinApprovalState, StablecoinTransfer, StablecoinTransferStatus } from '../../../models/transfer.model';
import { StablecoinDestination } from '../../../models/destination.model';
import {
	CancelStablecoinTransfer,
	ClearStablecoinTransfersState,
	CreateStablecoinDestination,
	CreateStablecoinTransfer,
	DisableStablecoinDestination,
	GetNeedsApprovalTransfers,
	GetStablecoinApprovalPolicy,
	GetStablecoinDestinations,
	GetStablecoinTransfers,
	GetTransferApprovals,
	InitStablecoinTransfersState,
	ResetStablecoinTransfersState,
	SubmitDestinationApproval,
	SubmitTransferApproval,
} from '../../actions/transfers.actions';

export interface StablecoinTransfersStateModel {
	transfers: StablecoinTransfer[] | null;
	transfersNextCursor: string | null;
	needsApproval: StablecoinTransfer[] | null;
	approvalsByTransferId: { [transferId: string]: StablecoinApprovalState[] };
	destinations: StablecoinDestination[] | null;
	policy: StablecoinApprovalPolicy | null;
	apiErrors: StateApiError[];
	isCached: boolean;
	transfersLoading: boolean;
	needsApprovalLoading: boolean;
	destinationsLoading: boolean;
	transferActionInFlight: boolean;
}

const DEFAULT_STABLECOIN_TRANSFERS_STATE: StablecoinTransfersStateModel = {
	transfers: null,
	transfersNextCursor: null,
	needsApproval: null,
	approvalsByTransferId: {},
	destinations: null,
	policy: null,
	apiErrors: [],
	isCached: false,
	transfersLoading: true,
	needsApprovalLoading: true,
	destinationsLoading: true,
	transferActionInFlight: false,
};

@State<StablecoinTransfersStateModel>({
	name: 'stablecoinTransfers',
	defaults: { ...DEFAULT_STABLECOIN_TRANSFERS_STATE },
})
@Injectable()
export class StablecoinTransfersState {
	private readonly transfersService: StablecoinTransfersService = inject(StablecoinTransfersService);

	@Selector()
	static transfers(state: StablecoinTransfersStateModel): StablecoinTransfer[] | null {
		return state.transfers;
	}

	@Selector()
	static transfersNextCursor(state: StablecoinTransfersStateModel): string | null {
		return state.transfersNextCursor;
	}

	@Selector()
	static needsApproval(state: StablecoinTransfersStateModel): StablecoinTransfer[] | null {
		return state.needsApproval;
	}

	@Selector()
	static needsApprovalCount(state: StablecoinTransfersStateModel): number {
		return state.needsApproval?.length ?? 0;
	}

	@Selector()
	static approvalsByTransferId(state: StablecoinTransfersStateModel): { [transferId: string]: StablecoinApprovalState[] } {
		return state.approvalsByTransferId;
	}

	@Selector()
	static destinations(state: StablecoinTransfersStateModel): StablecoinDestination[] | null {
		return state.destinations;
	}

	@Selector()
	static policy(state: StablecoinTransfersStateModel): StablecoinApprovalPolicy | null {
		return state.policy;
	}

	@Selector()
	static transfersLoading(state: StablecoinTransfersStateModel): boolean {
		return state.transfersLoading;
	}

	@Selector()
	static needsApprovalLoading(state: StablecoinTransfersStateModel): boolean {
		return state.needsApprovalLoading;
	}

	@Selector()
	static destinationsLoading(state: StablecoinTransfersStateModel): boolean {
		return state.destinationsLoading;
	}

	@Selector()
	static transferActionInFlight(state: StablecoinTransfersStateModel): boolean {
		return state.transferActionInFlight;
	}

	@Selector()
	static apiErrors(state: StablecoinTransfersStateModel): StateApiError[] {
		return state.apiErrors;
	}

	@Action(InitStablecoinTransfersState)
	init(): void {
		// Fetches are dispatched lazily from page components (no entitlement gate yet).
	}

	@Action(ClearStablecoinTransfersState)
	clear(context: StateContext<StablecoinTransfersStateModel>): void {
		context.setState({ ...DEFAULT_STABLECOIN_TRANSFERS_STATE });
	}

	@Action(ResetStablecoinTransfersState)
	reset(context: StateContext<StablecoinTransfersStateModel>): void {
		context.setState({ ...DEFAULT_STABLECOIN_TRANSFERS_STATE });
	}

	@Action(GetStablecoinTransfers)
	getTransfers(context: StateContext<StablecoinTransfersStateModel>, action: GetStablecoinTransfers): Observable<StablecoinPage<StablecoinTransfer>> {
		context.patchState({ transfersLoading: true });
		return this.transfersService.getTransfers(action.filter, action.cursor).pipe(
			tap((page: StablecoinPage<StablecoinTransfer>) => {
				checkToRemoveStateApiError(context, GetStablecoinTransfers);
				const state: StablecoinTransfersStateModel = context.getState();
				// Cursor fetches append; fresh fetches replace.
				const transfers: StablecoinTransfer[] = action.cursor ? [...(state.transfers ?? []), ...page.items] : page.items;
				context.patchState({ transfers, transfersNextCursor: page.next_cursor, transfersLoading: false, isCached: true });
			}),
			catchError((error: HttpErrorResponse) => {
				context.patchState({ transfersLoading: false });
				checkToAddStateApiError(error, context, GetStablecoinTransfers, checkForBadHttpErrorMessage(error, 'Unable to load transfers.').message);
				return throwError(() => error);
			})
		);
	}

	@Action(GetNeedsApprovalTransfers)
	getNeedsApproval(context: StateContext<StablecoinTransfersStateModel>): Observable<StablecoinPage<StablecoinTransfer>> {
		context.patchState({ needsApprovalLoading: true });
		return this.transfersService.getNeedsApprovalTransfers().pipe(
			tap((page: StablecoinPage<StablecoinTransfer>) => {
				checkToRemoveStateApiError(context, GetNeedsApprovalTransfers);
				context.patchState({ needsApproval: page.items, needsApprovalLoading: false });
			}),
			catchError((error: HttpErrorResponse) => {
				context.patchState({ needsApprovalLoading: false });
				checkToAddStateApiError(error, context, GetNeedsApprovalTransfers, checkForBadHttpErrorMessage(error, 'Unable to load pending approvals.').message);
				return throwError(() => error);
			})
		);
	}

	@Action(CreateStablecoinTransfer)
	createTransfer(context: StateContext<StablecoinTransfersStateModel>, action: CreateStablecoinTransfer): Observable<StablecoinTransfer> {
		context.patchState({ transferActionInFlight: true });
		return this.transfersService.createTransfer(action.body, action.idempotencyKey).pipe(
			tap((transfer: StablecoinTransfer) => {
				checkToRemoveStateApiError(context, CreateStablecoinTransfer);
				const state: StablecoinTransfersStateModel = context.getState();
				context.patchState({
					transfers: [transfer, ...(state.transfers ?? [])],
					needsApproval: transfer.status === StablecoinTransferStatus.pendingApproval ? [transfer, ...(state.needsApproval ?? [])] : state.needsApproval,
					transferActionInFlight: false,
				});
			}),
			catchError((error: HttpErrorResponse) => {
				context.patchState({ transferActionInFlight: false });
				checkToAddStateApiError(error, context, CreateStablecoinTransfer, checkForBadHttpErrorMessage(error, 'Unable to create transfer.').message);
				return throwError(() => error);
			})
		);
	}

	@Action(CancelStablecoinTransfer)
	cancelTransfer(context: StateContext<StablecoinTransfersStateModel>, action: CancelStablecoinTransfer): Observable<StablecoinTransfer> {
		context.patchState({ transferActionInFlight: true });
		return this.transfersService.cancelTransfer(action.transferId).pipe(
			tap((transfer: StablecoinTransfer) => {
				checkToRemoveStateApiError(context, CancelStablecoinTransfer);
				this.replaceTransfer(context, transfer);
				context.patchState({ transferActionInFlight: false });
			}),
			catchError((error: HttpErrorResponse) => {
				context.patchState({ transferActionInFlight: false });
				checkToAddStateApiError(error, context, CancelStablecoinTransfer, checkForBadHttpErrorMessage(error, 'Unable to cancel transfer.').message);
				return throwError(() => error);
			})
		);
	}

	@Action(GetTransferApprovals)
	getTransferApprovals(context: StateContext<StablecoinTransfersStateModel>, action: GetTransferApprovals): Observable<StablecoinApprovalState[]> {
		return this.transfersService.getTransferApprovals(action.transferId).pipe(
			tap((approvalStates: StablecoinApprovalState[]) => {
				checkToRemoveStateApiError(context, GetTransferApprovals);
				context.patchState({
					approvalsByTransferId: { ...context.getState().approvalsByTransferId, [action.transferId]: approvalStates },
				});
			}),
			catchError((error: HttpErrorResponse) => {
				checkToAddStateApiError(error, context, GetTransferApprovals, checkForBadHttpErrorMessage(error, 'Unable to load approvals.').message);
				return throwError(() => error);
			})
		);
	}

	@Action(SubmitTransferApproval)
	submitTransferApproval(context: StateContext<StablecoinTransfersStateModel>, action: SubmitTransferApproval): Observable<StablecoinTransfer> {
		context.patchState({ transferActionInFlight: true });
		return this.transfersService.submitTransferApproval(action.transferId, action.decision, action.comment).pipe(
			tap((transfer: StablecoinTransfer) => {
				checkToRemoveStateApiError(context, SubmitTransferApproval);
				this.replaceTransfer(context, transfer);
				context.patchState({ transferActionInFlight: false });
			}),
			catchError((error: HttpErrorResponse) => {
				context.patchState({ transferActionInFlight: false });
				checkToAddStateApiError(error, context, SubmitTransferApproval, checkForBadHttpErrorMessage(error, 'Unable to record decision.').message);
				return throwError(() => error);
			})
		);
	}

	@Action(GetStablecoinDestinations)
	getDestinations(context: StateContext<StablecoinTransfersStateModel>): Observable<StablecoinDestination[]> {
		context.patchState({ destinationsLoading: true });
		return this.transfersService.getDestinations().pipe(
			tap((destinations: StablecoinDestination[]) => {
				checkToRemoveStateApiError(context, GetStablecoinDestinations);
				context.patchState({ destinations, destinationsLoading: false });
			}),
			catchError((error: HttpErrorResponse) => {
				context.patchState({ destinationsLoading: false });
				checkToAddStateApiError(error, context, GetStablecoinDestinations, checkForBadHttpErrorMessage(error, 'Unable to load destinations.').message);
				return throwError(() => error);
			})
		);
	}

	@Action(CreateStablecoinDestination)
	createDestination(context: StateContext<StablecoinTransfersStateModel>, action: CreateStablecoinDestination): Observable<StablecoinDestination> {
		context.patchState({ transferActionInFlight: true });
		return this.transfersService.createDestination(action.body).pipe(
			tap((destination: StablecoinDestination) => {
				checkToRemoveStateApiError(context, CreateStablecoinDestination);
				context.patchState({
					destinations: [destination, ...(context.getState().destinations ?? [])],
					transferActionInFlight: false,
				});
			}),
			catchError((error: HttpErrorResponse) => {
				context.patchState({ transferActionInFlight: false });
				checkToAddStateApiError(error, context, CreateStablecoinDestination, checkForBadHttpErrorMessage(error, 'Unable to create destination.').message);
				return throwError(() => error);
			})
		);
	}

	@Action(SubmitDestinationApproval)
	submitDestinationApproval(context: StateContext<StablecoinTransfersStateModel>, action: SubmitDestinationApproval): Observable<StablecoinDestination> {
		context.patchState({ transferActionInFlight: true });
		return this.transfersService.submitDestinationApproval(action.destinationId, action.decision, action.comment).pipe(
			tap((destination: StablecoinDestination) => {
				checkToRemoveStateApiError(context, SubmitDestinationApproval);
				this.replaceDestination(context, destination);
				context.patchState({ transferActionInFlight: false });
			}),
			catchError((error: HttpErrorResponse) => {
				context.patchState({ transferActionInFlight: false });
				checkToAddStateApiError(
					error,
					context,
					SubmitDestinationApproval,
					checkForBadHttpErrorMessage(error, 'Unable to record destination decision.').message
				);
				return throwError(() => error);
			})
		);
	}

	@Action(DisableStablecoinDestination)
	disableDestination(context: StateContext<StablecoinTransfersStateModel>, action: DisableStablecoinDestination): Observable<StablecoinDestination> {
		context.patchState({ transferActionInFlight: true });
		return this.transfersService.disableDestination(action.destinationId).pipe(
			tap((destination: StablecoinDestination) => {
				checkToRemoveStateApiError(context, DisableStablecoinDestination);
				this.replaceDestination(context, destination);
				context.patchState({ transferActionInFlight: false });
			}),
			catchError((error: HttpErrorResponse) => {
				context.patchState({ transferActionInFlight: false });
				checkToAddStateApiError(error, context, DisableStablecoinDestination, checkForBadHttpErrorMessage(error, 'Unable to disable destination.').message);
				return throwError(() => error);
			})
		);
	}

	@Action(GetStablecoinApprovalPolicy)
	getPolicy(context: StateContext<StablecoinTransfersStateModel>): Observable<StablecoinApprovalPolicy> {
		return this.transfersService.getApprovalPolicy().pipe(
			tap((policy: StablecoinApprovalPolicy) => {
				checkToRemoveStateApiError(context, GetStablecoinApprovalPolicy);
				context.patchState({ policy });
			}),
			catchError((error: HttpErrorResponse) => {
				checkToAddStateApiError(error, context, GetStablecoinApprovalPolicy, checkForBadHttpErrorMessage(error, 'Unable to load approval policy.').message);
				return throwError(() => error);
			})
		);
	}

	private replaceTransfer(context: StateContext<StablecoinTransfersStateModel>, transfer: StablecoinTransfer): void {
		const state: StablecoinTransfersStateModel = context.getState();
		const mapTransfer = (item: StablecoinTransfer): StablecoinTransfer => (item.id === transfer.id ? transfer : item);
		context.patchState({
			transfers: state.transfers?.map(mapTransfer) ?? null,
			needsApproval:
				state.needsApproval?.map(mapTransfer).filter((item: StablecoinTransfer) => item.status === StablecoinTransferStatus.pendingApproval) ?? null,
		});
	}

	private replaceDestination(context: StateContext<StablecoinTransfersStateModel>, destination: StablecoinDestination): void {
		const state: StablecoinTransfersStateModel = context.getState();
		context.patchState({
			destinations: state.destinations?.map((item: StablecoinDestination) => (item.id === destination.id ? destination : item)) ?? null,
		});
	}
}
